import { useEffect, useState } from "react";
import { Check, Pause, Play, RotateCcw, Send } from "lucide-react";

const FieldGroup = ({ fields, value = {}, onChange }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const currentIndex = Math.min(activeIndex, fields.length - 1);

  if (!fields.length) return null;

  return (
    <div className="mt-4 rounded-lg bg-white p-4" data-testid="guided-reflection">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase text-[var(--sage-deep)]">Paso {currentIndex + 1} de {fields.length}</p>
        <div className="flex gap-1.5" role="group" aria-label="Pasos de la reflexión">
          {fields.map((label, index) => (
            <button key={label} type="button" aria-label={`Paso ${index + 1}: ${label}`} aria-current={currentIndex === index ? "step" : undefined}
              onClick={() => setActiveIndex(index)} className={`size-8 rounded-full text-sm font-semibold ${currentIndex === index ? "bg-[var(--forest)] text-white" : "bg-[var(--cream-2)] text-[var(--ink)]"}`}>
              {index + 1}
            </button>
          ))}
        </div>
      </div>
      <label className="mt-3 block font-semibold">
        {fields[currentIndex]}
        <textarea value={value[currentIndex] || ""} onChange={(event) => onChange(currentIndex, event.target.value)} rows={3}
          placeholder="Una idea breve es suficiente…"
          className="mt-2 w-full rounded-lg border-2 border-[#eadfce] bg-[var(--cream)] px-3 py-2 font-normal focus:border-[var(--forest)] focus:outline-none" />
      </label>
      <div className="mt-3 flex items-center justify-between">
        <button type="button" disabled={currentIndex === 0} onClick={() => setActiveIndex(currentIndex - 1)} className="text-sm font-semibold underline underline-offset-4 disabled:opacity-40">Anterior</button>
        <p className="text-xs text-[#7a6c5e]">Puedes dejar cualquier paso en blanco.</p>
        <button type="button" disabled={currentIndex === fields.length - 1} onClick={() => setActiveIndex(currentIndex + 1)} className="text-sm font-semibold underline underline-offset-4 disabled:opacity-40">Siguiente</button>
      </div>
    </div>
  );
};

export default function DayActivity({ day, notes, update, ticks, onTick }) {
  const [seconds, setSeconds] = useState(60);
  const [running, setRunning] = useState(false);
  const activity = day.activity;
  const fieldsValue = notes.activityFields || {};
  const setField = (index, value) => update({ activityFields: { ...fieldsValue, [index]: value } });
  const scenario = day.lesson.scenario;
  const scenarioChoice = Number.isInteger(notes.scenarioChoice) ? notes.scenarioChoice : null;

  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setInterval(() => setSeconds((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [running]);

  useEffect(() => {
    if (seconds === 0) setRunning(false);
  }, [seconds]);

  const renderFields = (fields) => <FieldGroup fields={fields} value={fieldsValue} onChange={setField} />;

  return (
    <section className="rounded-2xl bg-[var(--cream)] p-4 sm:p-5" data-testid="day-activity">
      <p className="text-xs font-semibold uppercase text-[var(--sage-deep)]">Primero, ensayemos</p>
      <section className="mt-3 rounded-lg border border-[var(--sand)] bg-white p-4" data-testid="day-scenario">
        <p className="text-xs font-semibold uppercase text-[var(--terra)]">1 · Ponte en situación</p>
        <p className="mt-2 font-semibold">{scenario.situation}</p>
        <p className="mt-1 text-xs text-[#7a6c5e]">Elige qué te gustaría probar. No hay nota; después veremos qué efecto puede tener cada respuesta.</p>
        <div className="mt-3 space-y-2" role="group" aria-label="Elige una respuesta para el caso práctico">
          {scenario.choices.map((choice, index) => (
            <button key={choice.text} type="button" data-testid={`scenario-choice-${index}`} aria-pressed={scenarioChoice === index}
              onClick={() => update({ scenarioChoice: index })}
              className={`w-full rounded-lg border-2 p-3 text-left text-sm transition-colors ${scenarioChoice === index ? "border-[var(--forest)] bg-[var(--cream-2)]" : "border-[#eadfce] hover:bg-[var(--cream)]"}`}>
              {choice.text}
            </button>
          ))}
        </div>
        {scenarioChoice !== null && <p role="status" data-testid="scenario-feedback" className="mt-3 rounded-lg border-l-4 border-[var(--forest)] bg-[var(--cream-2)] p-3 text-sm text-[#4a4038]">
          {scenarioChoice === 0 && <strong className="block mb-1">Una respuesta más asertiva en esta escena</strong>}
          {scenario.choices[scenarioChoice].feedback}
        </p>}
        {scenarioChoice === 0 && <details className="mt-2 rounded-lg bg-[var(--cream)] p-3" data-testid="assertiveness-explanation">
          <summary className="cursor-pointer text-sm font-semibold">¿Qué la hace asertiva?</summary>
          <p className="mt-2 text-sm text-[#4a4038]">Nombra una situación y una necesidad sin atacar, propone algo concreto y deja espacio a la respuesta de la otra persona. Ser asertivo no es conseguir un sí ni renunciar a tus límites. Si expresar una necesidad puede ponerte en riesgo, prioriza tu seguridad y busca apoyo individual.</p>
        </details>}
      </section>
      <details className="mt-3 rounded-lg border border-[var(--sand)] bg-white p-4" data-testid="day-everyday-example">
        <summary className="cursor-pointer text-sm font-semibold">Ver otro ejemplo cotidiano</summary>
        <p className="mt-2 text-sm text-[#4a4038]">{day.lesson.example}</p>
      </details>

      <div className="mt-6 border-t border-[var(--sand)] pt-5">
      <p className="text-xs font-semibold uppercase text-[var(--sage-deep)]">2 · Práctica guiada</p>
      <h3 className="font-display text-xl mt-1">{activity.title}</h3>
      <p className="mt-2 text-sm text-[#5a4f46]">{activity.prompt}</p>

      {activity.mode === "pause" && (
        <div className="mt-4 rounded-xl bg-white p-4" data-testid="pause-timer">
          <p className="font-display text-3xl tabular-nums" aria-live="polite">{`0:${String(seconds).padStart(2, "0")}`}</p>
          <p className="mt-1 text-sm text-[#5a4f46]">Respira con comodidad; puedes parar en cualquier momento.</p>
          <div className="mt-3 flex gap-2">
            <button type="button" onClick={() => setRunning((value) => !value)} className="btn-ghost !py-2 text-sm">{running ? <Pause size={16} /> : <Play size={16} />}{running ? "Pausar" : seconds === 0 ? "Repetir" : "Empezar"}</button>
            <button type="button" onClick={() => { setRunning(false); setSeconds(60); }} aria-label="Reiniciar temporizador" className="btn-ghost !py-2"><RotateCcw size={16} /></button>
          </div>
        </div>
      )}

      {activity.mode === "write" && (
        <textarea data-testid="activity-response" value={notes.activityText || ""} onChange={(event) => update({ activityText: event.target.value })} rows={4}
          placeholder="Escribe una idea breve para probar…" className="mt-4 w-full rounded-xl border-2 border-[#eadfce] bg-white px-3 py-2 focus:border-[var(--ink)] focus:outline-none" />
      )}

      {["fields", "observe", "review", "map", "rewrite", "pause-plan", "agreement"].includes(activity.mode) && (
        <div className="mt-4">{renderFields(activity.fields || ["Qué observé", "Qué necesito", "Qué quiero probar"])}</div>
      )}

      {activity.mode === "choice" && (
        <div className="mt-4 space-y-2" role="group" aria-label="Elige una opción para ensayar">
          {activity.options.map((option, index) => (
            <button key={option} type="button" aria-pressed={notes.activityChoice === index} onClick={() => update({ activityChoice: index })}
              className={`w-full rounded-xl border-2 p-3 text-left text-sm ${notes.activityChoice === index ? "border-[var(--sage-deep)] bg-white" : "border-[#eadfce] hover:bg-white"}`}>{option}</button>
          ))}
          {Number.isInteger(notes.activityChoice) && <p role="status" className="rounded-xl bg-white p-3 text-sm">Ahora practica formulando esa elección como una pregunta directa, sin dar por hecho la respuesta.</p>}
        </div>
      )}

      {activity.mode === "conversation" && (
        <div className="mt-4 rounded-xl bg-white p-4">
          <p className="text-lg">{day.question}</p>
          <a data-testid="share-question-button" href={`https://wa.me/?text=${encodeURIComponent(`Para hablar hoy: ${day.question}`)}`} target="_blank" rel="noreferrer" className="btn-ghost mt-3 !py-2 text-sm"><Send size={16} /> Compartir pregunta</a>
        </div>
      )}

      {["practice", "checklist"].includes(activity.mode) && (
        <div className="mt-4 space-y-2">
          {day.exercise.map((step, index) => (
            <button key={step} type="button" data-testid={`exercise-step-${index}`} onClick={() => onTick(index)} aria-pressed={ticks.includes(index)}
              className={`w-full rounded-xl border-2 p-3 text-left text-sm flex gap-3 ${ticks.includes(index) ? "bg-white border-[var(--sage-deep)]" : "border-[#eadfce] hover:bg-white"}`}>
              <span className="shrink-0 w-5 h-5 rounded-full border-2 grid place-items-center">{ticks.includes(index) && <Check size={13} />}</span>{step}
            </button>
          ))}
          <p className="text-xs text-[#7a6c5e]" data-testid="exercise-progress">{ticks.length} de {day.exercise.length} pasos</p>
        </div>
      )}

      <p className="mt-3 text-xs text-[#7a6c5e]">Tus respuestas se guardan solo en este dispositivo. No hace falta completar todos los pasos.</p>
      </div>
    </section>
  );
}